import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, FormControlLabel, InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { bonusTrackerAPI } from '../../services/api';
import { BonusFrequency, periodEndProblem, periodRange } from '../../utils/bonusPeriod';
import {
  BonusCategory, BonusDetail, BonusLine, Campaign, EmployeeOption, STAGES, STAGE_LABEL, Stage, employeeLabel, errText, money, todayYmd,
} from './bonusTypes';

interface Props {
  id: string;
  categories: BonusCategory[];
  campaigns: Campaign[];
  onClose: () => void;
  /** Called after any change so the list behind the window refreshes. */
  onChanged: () => void;
}

const when = (d?: string | null) => (d ? new Date(d).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '');
const EVENT_LABEL: Record<string, string> = {
  CREATED: 'Created', DETAILS_EDITED: 'Details edited', EMPLOYEE_ADDED: 'Employee added', EMPLOYEE_REMOVED: 'Employee removed',
  LINE_EDITED: 'Employee edited', DECISION: 'Decision', APPROVALS_CLEARED: 'Approvals cleared', INCLUDED_IN_PAYROLL: 'Paid in payroll', DELETED: 'Deleted',
};

/** The bonus window: details, employees with their amount and reason, and the ordered approvals. */
const BonusDetailDialog: React.FC<Props> = ({ id, categories, campaigns, onClose, onChanged }) => {
  const [detail, setDetail] = useState<BonusDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  // details form
  const [categoryId, setCategoryId] = useState('');
  const [frequency, setFrequency] = useState<BonusFrequency>('MONTHLY');
  const [periodEnd, setPeriodEnd] = useState('');
  const [campaignId, setCampaignId] = useState('ALL');
  const [customerPaid, setCustomerPaid] = useState(false);
  const [concept, setConcept] = useState('');
  // employee edits
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [removing, setRemoving] = useState<BonusLine | null>(null);
  const [removeReason, setRemoveReason] = useState('');
  const [options, setOptions] = useState<EmployeeOption[]>([]);
  const [toAdd, setToAdd] = useState<EmployeeOption[]>([]);
  const [addAmount, setAddAmount] = useState<Record<string, string>>({});
  // decision form
  const [date, setDate] = useState(todayYmd());
  const [note, setNote] = useState('');
  const [customerAnswer, setCustomerAnswer] = useState<'Y' | 'N'>('Y');
  const [customerDate, setCustomerDate] = useState(todayYmd());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [everyone, setEveryone] = useState(false);

  const fill = useCallback((d: BonusDetail) => {
    setDetail(d);
    setCategoryId(d.categoryId);
    setFrequency(d.frequency === 'WEEKLY' ? 'WEEKLY' : 'MONTHLY');
    setPeriodEnd(d.periodEnd);
    setCampaignId(d.campaignId || 'ALL');
    setCustomerPaid(d.offeredBy === 'CUSTOMER');
    setConcept(d.concept || '');
    setAmounts(Object.fromEntries(d.lines.map((l) => [l.id, String(l.amount)])));
    setReasons(Object.fromEntries(d.lines.map((l) => [l.id, l.concept || ''])));
  }, []);

  const load = useCallback(async () => {
    try {
      fill(await bonusTrackerAPI.record(id));
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, [id, fill]);
  useEffect(() => { void load(); }, [load]);

  const perm = detail?.permissions;
  const editable = Boolean(perm?.canEdit);
  const lineStatusActive = (detail?.lines || []).filter((l) => l.status === 'ACTIVE');

  useEffect(() => {
    if (!editable) return;
    bonusTrackerAPI.employees('', detail?.campaignId || 'ALL', { from: detail?.periodStart, to: detail?.periodEnd }, everyone).then(setOptions).catch(() => undefined);
  }, [editable, detail?.campaignId, detail?.periodStart, detail?.periodEnd, everyone]);

  /** Runs a change, shows the new state, and refreshes the list behind. */
  const act = async (fn: () => Promise<any>, after?: () => void) => {
    setBusy(true);
    setError(null);
    try {
      const result = await fn();
      fill((result && result.record) || result);
      onChanged();
      after?.();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const problem = periodEnd ? periodEndProblem(frequency, periodEnd) : null;
  const range = periodEnd && !problem ? periodRange(frequency, periodEnd) : null;
  const detailsChanged = detail && (
    categoryId !== detail.categoryId || frequency !== (detail.frequency === 'WEEKLY' ? 'WEEKLY' : 'MONTHLY') || periodEnd !== detail.periodEnd
    || (campaignId === 'ALL' ? null : campaignId) !== detail.campaignId || customerPaid !== (detail.offeredBy === 'CUSTOMER') || (concept || '') !== (detail.concept || '')
  );

  const saveDetails = () => act(() => bonusTrackerAPI.updateRecord(id, {
    categoryId, frequency, periodEnd, campaignId: campaignId === 'ALL' ? null : campaignId, offeredBy: customerPaid ? 'CUSTOMER' : 'IN_HOUSE', concept: concept || null,
  }));

  const saveLine = (line: BonusLine) => {
    const amount = Number(amounts[line.id]);
    const reason = reasons[line.id] ?? '';
    const patch: { amount?: number; concept?: string } = {};
    if (amount !== line.amount) patch.amount = amount;
    if (reason !== (line.concept || '')) patch.concept = reason;
    if (!Object.keys(patch).length) return;
    if (patch.amount !== undefined && !(amount > 0)) { setError('Amount must be greater than zero.'); return; }
    void act(() => bonusTrackerAPI.updateLine(line.id, patch));
  };

  const addEmployees = () => act(
    () => bonusTrackerAPI.addLines(id, toAdd.map((u) => ({ employeeId: u.id, amount: Number(addAmount[u.id]), concept: null }))),
    () => { setToAdd([]); setAddAmount({}); },
  );

  const customerStage = detail?.offeredBy === 'CUSTOMER';
  const decide = (stage: Stage, decision: 'Y' | 'N') => act(
    () => bonusTrackerAPI.decide(id, {
      stage, decision, date, note: note || undefined,
      ...(customerStage && (stage === 'SUPERVISOR' || stage === 'ACCOUNTING') ? { customerAnswer, customerDate } : {}),
    }),
    () => { setNote(''); setDate(todayYmd()); },
  );

  const [cancelling, setCancelling] = useState<Stage | null>(null);
  const [cancelNote, setCancelNote] = useState('');
  const cancelApproval = () => {
    if (!cancelling) return;
    void act(() => bonusTrackerAPI.cancelDecision(id, { stage: cancelling, note: cancelNote }), () => { setCancelling(null); setCancelNote(''); });
  };

  const remove = () => {
    if (!removing) return;
    void act(() => bonusTrackerAPI.removeLine(removing.id, removeReason), () => { setRemoving(null); setRemoveReason(''); });
  };

  const del = async () => {
    setBusy(true);
    try {
      await bonusTrackerAPI.deleteRecord(id);
      onChanged();
      onClose();
    } catch (e) {
      setError(errText(e));
      setBusy(false);
    }
  };

  const stageCard = (s: BonusDetail['stages'][number]) => {
    const active = perm?.actionableStage === s.stage;
    const isNext = detail?.nextStage === s.stage;
    const isPresident = s.stage === 'PRESIDENT';
    const canCancel = perm?.cancellableStage === s.stage;
    const color = s.decision === 'Y' ? 'success' : s.decision === 'N' ? 'error' : isNext ? 'warning' : 'default';
    const customerLine = s.stage === 'SUPERVISOR' && customerStage
      ? `Customer approved: ${detail?.customer.approved === 'Y' ? 'Yes' : detail?.customer.approved === 'N' ? 'No' : 'pending'}${detail?.customer.approvedDate ? ` on ${detail.customer.approvedDate}` : ''}`
      : s.stage === 'ACCOUNTING' && customerStage
        ? `Customer paid TEKS: ${detail?.customer.paid === 'Y' ? 'Yes' : detail?.customer.paid === 'N' ? 'No' : 'pending'}${detail?.customer.paidDate ? ` on ${detail.customer.paidDate}` : ''}`
        : null;
    return (
      <Paper key={s.stage} variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 200, borderColor: isNext ? 'warning.main' : undefined }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontWeight: 700 }}>{STAGES.indexOf(s.stage) + 1}. {STAGE_LABEL[s.stage]}</Typography>
          <Chip size="small" color={color as any} variant={s.decision ? 'filled' : 'outlined'}
            label={s.decision === 'Y' ? 'Approved' : s.decision === 'N' ? 'Rejected' : isNext ? 'Waiting' : 'Not yet'} />
        </Stack>
        {s.decision && !isPresident && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {s.byName || 'Unknown'} · {s.date || ''}{s.note ? ` · ${s.note}` : ''}
          </Typography>
        )}
        {customerLine && <Typography variant="caption" display="block">{customerLine}</Typography>}
        {isNext && !active && !isPresident && perm?.blockedReason && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>{perm.blockedReason}</Typography>
        )}
        {canCancel && (
          <Button size="small" color="warning" variant="outlined" sx={{ mt: 1 }} disabled={busy} onClick={() => { setCancelling(s.stage); setCancelNote(''); }}>
            Cancel {s.decision === 'N' ? 'rejection' : 'approval'}
          </Button>
        )}
        {active && (
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            <TextField size="small" type="date" label="Date" InputLabelProps={{ shrink: true }} value={date} onChange={(e) => setDate(e.target.value)} />
            {customerStage && (s.stage === 'SUPERVISOR' || s.stage === 'ACCOUNTING') && (
              <>
                <Typography variant="caption">{s.stage === 'SUPERVISOR' ? 'Did the customer approve this bonus?' : 'Did the customer pay TEKS for it?'}</Typography>
                <ToggleButtonGroup exclusive size="small" value={customerAnswer} onChange={(_e, v) => v && setCustomerAnswer(v)}>
                  <ToggleButton value="Y">Yes</ToggleButton>
                  <ToggleButton value="N">No</ToggleButton>
                </ToggleButtonGroup>
                <TextField size="small" type="date" label={s.stage === 'SUPERVISOR' ? 'Customer approval date' : 'Customer payment date'} InputLabelProps={{ shrink: true }}
                  value={customerDate} onChange={(e) => setCustomerDate(e.target.value)} />
              </>
            )}
            <TextField size="small" multiline minRows={2} label="Notes (required if you reject)" value={note} onChange={(e) => setNote(e.target.value)} />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" size="small" disabled={busy} onClick={() => decide(s.stage, 'Y')}>Approve</Button>
              <Button variant="outlined" color="error" size="small" disabled={busy || !note.trim()} onClick={() => decide(s.stage, 'N')}>Reject</Button>
            </Stack>
          </Stack>
        )}
      </Paper>
    );
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {detail ? `${detail.categoryName} · ${detail.frequency === 'WEEKLY' ? 'week' : detail.frequency === 'MONTHLY' ? 'month' : ''} ending ${detail.periodEnd}` : 'Bonus'}
        {detail && (
          <Chip size="small" sx={{ ml: 1.5 }} color={detail.status === 'APPROVED' ? 'success' : detail.status === 'REJECTED' ? 'error' : 'warning'}
            label={detail.status === 'APPROVED' ? 'Approved' : detail.status === 'REJECTED' ? 'Rejected' : 'In approval'} />
        )}
        {detail?.includedInPayroll && <Chip size="small" color="info" sx={{ ml: 1 }} label="Paid in payroll" />}
      </DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2.5, pt: '8px !important' }}>
        {loading && <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress size={28} /></Box>}
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {detail && perm && !editable && perm.readOnlyReason && <Alert severity="info">{perm.readOnlyReason}</Alert>}
        {detail && editable && perm?.editClearsApprovals && (
          <Alert severity="warning">An approval was already given. Any change you make here clears the approvals, and the approvers start again.</Alert>
        )}

        {detail && (
          <>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Details</Typography>
              {editable ? (
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Category</InputLabel>
                      <Select label="Category" value={categoryId} onChange={(e) => setCategoryId(String(e.target.value))}>
                        {categories.concat(categories.some((c) => c.id === detail.categoryId) ? [] : [{ id: detail.categoryId, name: detail.categoryName } as BonusCategory])
                          .map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <ToggleButtonGroup exclusive size="small" value={frequency} onChange={(_e, v) => { if (v) { setFrequency(v); } }}>
                      <ToggleButton value="WEEKLY">Weekly</ToggleButton>
                      <ToggleButton value="MONTHLY">Monthly</ToggleButton>
                    </ToggleButtonGroup>
                    <TextField size="small" type="date" fullWidth label="Last day of the period" InputLabelProps={{ shrink: true }} value={periodEnd}
                      onChange={(e) => setPeriodEnd(e.target.value)} error={Boolean(problem)} helperText={problem || (range ? `Covers ${range.start} to ${range.end}` : '')} />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Campaign</InputLabel>
                      <Select label="Campaign" value={campaignId} onChange={(e) => setCampaignId(String(e.target.value))}>
                        <MenuItem value="ALL">All campaigns</MenuItem>
                        {campaigns.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <FormControlLabel sx={{ whiteSpace: 'nowrap' }} control={<Checkbox checked={customerPaid} onChange={(e) => setCustomerPaid(e.target.checked)} />} label="Paid by the customer" />
                  </Stack>
                  <TextField size="small" label="General concept" value={concept} onChange={(e) => setConcept(e.target.value)} />
                  <Box><Button variant="contained" size="small" disabled={busy || !detailsChanged || Boolean(problem)} onClick={saveDetails}>Save details</Button></Box>
                </Stack>
              ) : (
                <Typography variant="body2">
                  {detail.campaignName} · {detail.offeredBy === 'CUSTOMER' ? 'paid by the customer' : 'paid in house'} · {detail.periodStart} to {detail.periodEnd}
                  {detail.concept ? ` · ${detail.concept}` : ''} · created by {detail.createdByName || 'unknown'}
                </Typography>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Employees ({lineStatusActive.length}) · total {money(detail.total, detail.currency)}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell align="right" sx={{ width: 150 }}>Amount ({detail.currency})</TableCell>
                    <TableCell>Why this employee gets it</TableCell>
                    <TableCell sx={{ width: 130 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.lines.map((l) => {
                    const removed = l.status === 'REMOVED';
                    return (
                      <TableRow key={l.id} sx={removed ? { opacity: 0.55 } : undefined}>
                        <TableCell sx={removed ? { textDecoration: 'line-through' } : undefined}>
                          {l.employeeName}
                          <Typography variant="caption" display="block" color="text.secondary">{l.employeeNumber}{l.includedInPayroll ? ' · paid in payroll' : ''}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          {editable && !removed ? (
                            <TextField size="small" type="number" value={amounts[l.id] ?? ''} sx={{ width: 120 }}
                              onChange={(e) => setAmounts((p) => ({ ...p, [l.id]: e.target.value }))} onBlur={() => saveLine(l)} />
                          ) : money(l.amount, detail.currency)}
                        </TableCell>
                        <TableCell>
                          {editable && !removed ? (
                            <TextField size="small" fullWidth value={reasons[l.id] ?? ''} placeholder="Reason"
                              onChange={(e) => setReasons((p) => ({ ...p, [l.id]: e.target.value }))} onBlur={() => saveLine(l)} />
                          ) : (
                            <>
                              {l.concept || <Typography variant="caption" color="text.secondary">-</Typography>}
                              {removed && (
                                <Typography variant="caption" color="error" display="block">
                                  Removed by {l.removedByName || 'unknown'}{l.removedAt ? ` on ${when(l.removedAt)}` : ''}: {l.removedReason}
                                </Typography>
                              )}
                            </>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {!removed && perm?.canRemoveEmployee && (
                            <Button size="small" color="warning" disabled={busy} onClick={() => { setRemoving(l); setRemoveReason(''); }}>Remove</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {editable && (
                <Stack spacing={1} sx={{ mt: 2 }}>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    size="small"
                    options={options.filter((o) => !lineStatusActive.some((l) => l.employeeId === o.id))}
                    value={toAdd}
                    getOptionLabel={employeeLabel}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    filterOptions={(x) => x}
                    onInputChange={(_e, value, reason) => {
                      if (reason === 'input') bonusTrackerAPI.employees(value, detail.campaignId || 'ALL', { from: detail.periodStart, to: detail.periodEnd }, everyone).then(setOptions).catch(() => undefined);
                    }}
                    onChange={(_e, list) => {
                      setToAdd(list);
                      setAddAmount((prev) => {
                        const next = { ...prev };
                        const base = categories.find((c) => c.id === detail.categoryId)?.defaultAmount;
                        list.forEach((u) => { if (next[u.id] === undefined) next[u.id] = base != null ? String(base) : ''; });
                        return next;
                      });
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} key={option.id}><Checkbox size="small" checked={selected} sx={{ mr: 1 }} />{employeeLabel(option)}</li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Add employees to this bonus" />}
                  />
                  {detail.campaignId && (
                    <FormControlLabel
                      control={<Checkbox size="small" checked={everyone} onChange={(e) => setEveryone(e.target.checked)} />}
                      label="Show every employee, not only the ones assigned to this campaign in this period"
                    />
                  )}
                  {toAdd.map((u) => (
                    <Stack key={u.id} direction="row" spacing={1} alignItems="center">
                      <Typography sx={{ flexGrow: 1 }}>{employeeLabel(u)}</Typography>
                      <TextField size="small" type="number" label="Amount" sx={{ width: 140 }} value={addAmount[u.id] ?? ''}
                        onChange={(e) => setAddAmount((p) => ({ ...p, [u.id]: e.target.value }))} />
                    </Stack>
                  ))}
                  {toAdd.length > 0 && (
                    <Box>
                      <Button variant="contained" size="small" disabled={busy || !toAdd.every((u) => Number(addAmount[u.id]) > 0)} onClick={addEmployees}>
                        Add {toAdd.length} employee(s)
                      </Button>
                    </Box>
                  )}
                </Stack>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Approvals (in order, no step can be skipped; cancel newest first)</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>{detail.stages.map(stageCard)}</Stack>
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>History</Typography>
              {detail.events.map((e, i) => (
                <Typography key={i} variant="caption" display="block" color="text.secondary">
                  {when(e.createdAt)} · {e.actorName || 'System'} · {EVENT_LABEL[e.action] || e.action}
                  {e.stage ? ` · ${STAGE_LABEL[e.stage as Stage] || e.stage}${e.decision ? ` ${e.decision === 'Y' ? 'approved' : 'rejected'}` : ''}` : ''}
                  {e.override ? ' (super user override)' : ''}{e.note ? ` · ${e.note}` : ''}
                </Typography>
              ))}
            </Box>
          </>
        )}

        <Dialog open={Boolean(removing)} onClose={() => setRemoving(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Remove {removing?.employeeName}</DialogTitle>
          <DialogContent sx={{ pt: '8px !important' }}>
            <TextField fullWidth autoFocus multiline minRows={2} label="Why is this employee removed?" value={removeReason} onChange={(e) => setRemoveReason(e.target.value)} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRemoving(null)}>Cancel</Button>
            <Button color="warning" variant="contained" disabled={busy || !removeReason.trim()} onClick={remove}>Remove</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(cancelling)} onClose={() => setCancelling(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Cancel the {cancelling ? STAGE_LABEL[cancelling].toLowerCase() : ''} {detail?.stages.find((x) => x.stage === cancelling)?.decision === 'N' ? 'rejection' : 'approval'}</DialogTitle>
          <DialogContent sx={{ pt: '8px !important' }}>
            <TextField fullWidth autoFocus multiline minRows={2} label="Why is it cancelled?" value={cancelNote} onChange={(e) => setCancelNote(e.target.value)} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelling(null)}>Close</Button>
            <Button color="warning" variant="contained" disabled={busy || !cancelNote.trim()} onClick={cancelApproval}>Cancel it</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Delete this bonus?</DialogTitle>
          <DialogContent><Typography variant="body2">The whole bonus and its employees are deleted. The history of what happened stays on record.</Typography></DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button color="error" variant="contained" disabled={busy} onClick={del}>Delete</Button>
          </DialogActions>
        </Dialog>
      </DialogContent>
      <DialogActions>
        {perm?.canDelete && <Button color="error" disabled={busy} onClick={() => setConfirmDelete(true)}>Delete bonus</Button>}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BonusDetailDialog;
